import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Image as ImageIcon,
  Clock,
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  Upload,
  Plus,
} from "lucide-react";
import { API } from "@/App";
import GoogleMapLocationPicker from "@/components/GoogleMapLocationPicker";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

const STEPS_KEYS = [
  { id: 1, titleKey: "settings:basicInfo", icon: Building2 },
  { id: 2, titleKey: "common:location", icon: MapPin },
  { id: 3, titleKey: "common:branchesAndServices", icon: Clock },
  { id: 4, titleKey: "common:images", icon: ImageIcon },
];

// Training types will be fetched from API dynamically
const DEFAULT_TRAINING_TYPES = [
  { id: "Birebir", label: "Birebir" },
  { id: "Düet", label: "Düet" },
  { id: "Grup", label: "Grup" },
];

// Türkiye İlleri ve İlçeleri
const PRIORITY_CITIES = [
  { name: "İstanbul", code: "istanbul", lat: 41.0082, lng: 28.9784 },
  { name: "Ankara", code: "ankara", lat: 39.9334, lng: 32.8597 },
  { name: "İzmir", code: "izmir", lat: 38.4237, lng: 27.1428 },
];

const TURKEY_CITIES = [
  "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Aksaray", "Amasya", "Antalya", "Ardahan",
  "Artvin", "Aydın", "Balıkesir", "Bartın", "Batman", "Bayburt", "Bilecik", "Bingöl",
  "Bitlis", "Bolu", "Burdur", "Bursa", "Çanakkale", "Çankırı", "Çorum", "Denizli",
  "Diyarbakır", "Düzce", "Edirne", "Elazığ", "Erzincan", "Erzurum", "Eskişehir",
  "Gaziantep", "Giresun", "Gümüşhane", "Hakkari", "Hatay", "Iğdır", "Isparta",
  "Kahramanmaraş", "Karabük", "Karaman", "Kars", "Kastamonu", "Kayseri", "Kırıkkale",
  "Kırklareli", "Kırşehir", "Kilis", "Kocaeli", "Konya", "Kütahya", "Malatya", "Manisa",
  "Mardin", "Mersin", "Muğla", "Muş", "Nevşehir", "Niğde", "Ordu", "Osmaniye", "Rize",
  "Sakarya", "Samsun", "Siirt", "Sinop", "Sivas", "Şanlıurfa", "Şırnak", "Tekirdağ",
  "Tokat", "Trabzon", "Tunceli", "Uşak", "Van", "Yalova", "Yozgat", "Zonguldak"
].sort((a, b) => a.localeCompare(b, 'tr'));

// İstanbul ilçeleri (birleşik)
const ISTANBUL_DISTRICTS = [
  "Adalar", "Arnavutköy", "Ataşehir", "Avcılar", "Bağcılar", "Bahçelievler", "Bakırköy", 
  "Başakşehir", "Bayrampaşa", "Beşiktaş", "Beykoz", "Beylikdüzü", "Beyoğlu", 
  "Büyükçekmece", "Çatalca", "Çekmeköy", "Esenler", "Esenyurt", "Eyüpsultan", 
  "Fatih", "Gaziosmanpaşa", "Güngören", "Kadıköy", "Kağıthane", "Kartal", 
  "Küçükçekmece", "Maltepe", "Pendik", "Sancaktepe", "Sarıyer", "Silivri", 
  "Sultanbeyli", "Sultangazi", "Şile", "Şişli", "Tuzla", "Ümraniye", "Üsküdar", 
  "Zeytinburnu"
].sort((a, b) => a.localeCompare(b, 'tr'));

const ANKARA_DISTRICTS = [
  "Akyurt", "Altındağ", "Ayaş", "Bala", "Beypazarı", "Çamlıdere", "Çankaya",
  "Çubuk", "Elmadağ", "Etimesgut", "Evren", "Gölbaşı", "Güdül", "Haymana",
  "Kahramankazan", "Kalecik", "Keçiören", "Kızılcahamam", "Mamak", "Nallıhan",
  "Polatlı", "Pursaklar", "Sincan", "Şereflikoçhisar", "Yenimahalle"
].sort((a, b) => a.localeCompare(b, 'tr'));

const IZMIR_DISTRICTS = [
  "Aliağa", "Balçova", "Bayındır", "Bayraklı", "Bergama", "Beydağ", "Bornova",
  "Buca", "Çeşme", "Çiğli", "Dikili", "Foça", "Gaziemir", "Güzelbahçe",
  "Karabağlar", "Karaburun", "Karşıyaka", "Kemalpaşa", "Kınık", "Kiraz",
  "Konak", "Menderes", "Menemen", "Narlıdere", "Ödemiş", "Seferihisar",
  "Selçuk", "Tire", "Torbalı", "Urla"
].sort((a, b) => a.localeCompare(b, 'tr'));

// İlçe verisi döndüren fonksiyon
const getDistrictsForCity = (cityCode) => {
  switch (cityCode) {
    case "istanbul":
      return ISTANBUL_DISTRICTS;
    case "ankara":
      return ANKARA_DISTRICTS;
    case "izmir":
      return IZMIR_DISTRICTS;
    default:
      return [];
  }
};

// Default cards - will be replaced by API data
const DEFAULT_ACCEPTED_CARDS = [
  "MultiSport",
  "Gympass",
  "Mediatopia",
  "Passo",
];

export default function RegisterBusinessPage({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = user || location.state?.user;
  
  // Check if this is branch creation (parent query param exists)
  const searchParams = new URLSearchParams(location.search);
  const parentBusinessId = searchParams.get('parent');
  const isBranchCreation = !!parentBusinessId;
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "employees"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();


  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [trainingTypes, setTrainingTypes] = useState(DEFAULT_TRAINING_TYPES);
  const [acceptedCards, setAcceptedCards] = useState(DEFAULT_ACCEPTED_CARDS);
  const [districts, setDistricts] = useState([]);
  
  // Parent business info for branch creation
  const [parentBusiness, setParentBusiness] = useState(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      toast.error(t("common:toast_i̇sletmeKaydiIcinOnceGiris"));
      navigate("/auth", { state: { returnTo: "/register-business" } });
    }
  }, [currentUser, navigate]);

  // Form state - default branches eklendi, business_type kurumsal yapıldı
  const [formData, setFormData] = useState({
    name: "",
    business_type: "corporate", // Her zaman kurumsal
    phone: "",
    email: "",
    city: "",
    district: "",
    neighborhood: "",
    street: "",
    building_no: "",
    address: "",
    latitude: 41.0082,
    longitude: 28.9784,
    branches: [], // Kullanıcı seçsin
    training_types: [],
    accepted_cards: [],
    working_hours: {},
    images: [],
  });

  // Working hours state
  const [workingHours, setWorkingHours] = useState({
    monday: { open: "09:00", close: "21:00", isOpen: true },
    tuesday: { open: "09:00", close: "21:00", isOpen: true },
    wednesday: { open: "09:00", close: "21:00", isOpen: true },
    thursday: { open: "09:00", close: "21:00", isOpen: true },
    friday: { open: "09:00", close: "21:00", isOpen: true },
    saturday: { open: "09:00", close: "18:00", isOpen: true },
    sunday: { open: "10:00", close: "16:00", isOpen: false },
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Şehir değiştiğinde ilçeleri güncelle
  useEffect(() => {
    if (formData.city) {
      const cityDistricts = getDistrictsForCity(formData.city);
      setDistricts(cityDistricts);
      // Şehir değiştiğinde ilçeyi sıfırla
      if (formData.district && !cityDistricts.includes(formData.district)) {
        updateFormData("district", "");
      }
    } else {
      setDistricts([]);
    }
  }, [formData.city]);

  const fetchData = async () => {
    try {
      const [branchesRes, trainingTypesRes, cardsRes] = await Promise.all([
        fetch(`${API}/public/branches`),
        fetch(`${API}/public/training-types`),
        fetch(`${API}/public/member-cards`)
      ]);
      
      if (branchesRes.ok) {
        const data = await branchesRes.json();
        // Normalize branches
        const normalizedBranches = (Array.isArray(data) ? data : []).map(b => {
          if (typeof b === 'string') return { id: b, name: b, is_active: true };
          return b;
        });
        setBranches(normalizedBranches);
      }
      
      if (trainingTypesRes.ok) {
        const data = await trainingTypesRes.json();
        const types = data.training_types || data || [];
        if (types.length > 0) {
          // Convert to object array with consistent format
          const typeObjects = types.map(t => {
            if (typeof t === 'string') return { id: t, name: t, label: t };
            return { 
              id: t.id || t.name || t, 
              name: t.name || t.label || t.id || t,
              label: t.label || t.name || t.id || t
            };
          });
          setTrainingTypes(typeObjects);
        }
      }
      
      if (cardsRes.ok) {
        const data = await cardsRes.json();
        const cards = data.member_cards || data || [];
        if (cards.length > 0) {
          // Normalize to string array
          const cardNames = cards.map(c => typeof c === 'string' ? c : (c.name || c.card_name || c.id || String(c)));
          setAcceptedCards(cardNames);
        }
      }
      
      // If creating a branch, fetch parent business info
      if (parentBusinessId) {
        const parentRes = await fetch(`${API}/businesses/${parentBusinessId}`, {
          credentials: "include"
        });
        if (parentRes.ok) {
          const parentData = await parentRes.json();
          setParentBusiness(parentData);
          // Pre-fill business type from parent
          updateFormData("business_type", parentData.business_type || "individual");
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleBranch = (branch) => {
    setFormData((prev) => ({
      ...prev,
      branches: prev.branches.includes(branch)
        ? prev.branches.filter((b) => b !== branch)
        : [...prev.branches, branch],
    }));
  };

  const toggleTrainingType = (type) => {
    setFormData((prev) => ({
      ...prev,
      training_types: prev.training_types.includes(type)
        ? prev.training_types.filter((t) => t !== type)
        : [...prev.training_types, type],
    }));
  };

  const toggleCard = (card) => {
    setFormData((prev) => ({
      ...prev,
      accepted_cards: prev.accepted_cards.includes(card)
        ? prev.accepted_cards.filter((c) => c !== card)
        : [...prev.accepted_cards, card],
    }));
  };

  const updateWorkingHour = (day, field, value) => {
    setWorkingHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  // Resize image before upload
  const resizeImage = (file, maxWidth = 1200, maxHeight = 800, quality = 0.85) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          
          // Calculate new dimensions maintaining aspect ratio
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = false;
    
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      // Check file size (max 10MB before resize)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t("common:toast_dosyaBoyutu10mbdanKucukOlmali"));
        return;
      }
      
      try {
        // Resize image before upload
        toast.loading(t("employees:optimizingImage"), { id: "upload" });
        const resizedFile = await resizeImage(file, 1200, 800, 0.85);
        
        const formDataUpload = new FormData();
        formDataUpload.append('file', resizedFile);
        
        toast.loading(t("common:uploadingImage"), { id: "upload" });
        
        // Use public-image endpoint for registration (no auth required)
        const response = await fetch(`${API}/upload/public-image`, {
          method: 'POST',
          body: formDataUpload,
        });
        
        toast.dismiss("upload");
        
        if (response.ok) {
          const data = await response.json();
          // ImgBB returns full URL directly, no need to construct
          const imageUrl = data.url;
          setFormData((prev) => ({
            ...prev,
            images: [...prev.images, imageUrl],
          }));
          toast.success(t("common:toast_gorselEklendi_8e78"));
        } else {
          const error = await response.json();
          toast.error(error.detail || t("common:toast_gorselYuklenemedi"));
        }
      } catch (error) {
        toast.dismiss("upload");
        toast.error(t('common:imageUploadError'));
      }
    };
    
    input.click();
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) {
          toast.error(t("common:toast_i̇sletmeAdiZorunludur"));
          return false;
        }
        if (!formData.phone.trim()) {
          toast.error(t('common:phoneRequired'));
          return false;
        }
        return true;
      case 2:
        // Google Maps adresi yeterli - şehir/ilçe otomatik alınacak
        if (!formData.address.trim()) {
          toast.error(t("common:toast_haritadanKonumSecinVeyaAdres"));
          return false;
        }
        return true;
      case 3:
        if (formData.branches.length === 0) {
          toast.error(t('common:selectAtLeastOneBranch'));
          return false;
        }
        if (formData.training_types.length === 0) {
          toast.error(t("common:toast_enAzBirAntrenmanTipi"));
          return false;
        }
        return true;
      case 4:
        if (formData.images.length === 0) {
          toast.error(t("common:toast_enAzBirGorselEklemelisiniz"));
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS_KEYS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    // Check if user is authenticated
    if (!currentUser) {
      toast.error(t("common:toast_i̇sletmeKaydiIcinGirisYapmaniz"));
      navigate("/auth", { state: { returnTo: "/register-business" } });
      return;
    }
    
    setLoading(true);

    // Prepare working hours
    const formattedWorkingHours = {};
    Object.entries(workingHours).forEach(([day, hours]) => {
      if (hours.isOpen) {
        formattedWorkingHours[day] = {
          open: hours.open,
          close: hours.close,
        };
      }
    });

    try {
      // Şehir adını düzgün formatla
      let cityName = formData.city;
      const priorityCity = PRIORITY_CITIES.find(c => c.code === formData.city);
      if (priorityCity) {
        cityName = priorityCity.name;
      } else {
        // İlk harfi büyük yap
        cityName = formData.city.charAt(0).toUpperCase() + formData.city.slice(1);
      }
      
      // Use different endpoint for branch creation vs main business registration
      // Always use public endpoint for initial business registration (not branch creation)
      let endpoint;
      if (isBranchCreation) {
        endpoint = `${API}/businesses/${parentBusinessId}/branches`;
      } else {
        // For main business registration, always use public endpoint
        // This works for both new users and users without a business yet
        endpoint = `${API}/public/register-business`;
      }
      
      // Get token from localStorage or state (for cross-origin requests)
      const token = localStorage.getItem("auth_token") || location.state?.token;
      
      // Build headers with Authorization if token exists
      const headers = { "Content-Type": "application/json" };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          city: cityName,
          working_hours: formattedWorkingHours,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t("common:businessSaveFailed"));
      }

      const data = await response.json();
      
      // Show approval pending message
      if (isBranchCreation) {
        toast.success(
          t("admin:branchRegistered"), 
          { duration: 5000 }
        );
      } else {
        toast.success(
          t("admin:businessRegistered"), 
          { duration: 5000 }
        );
      }
      toast.info(
        t("admin:businessUnderReview"), 
        { duration: 7000 }
      );
      
      navigate("/dashboard/branches", { state: { user: currentUser, businessPending: true } });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="name">{t("business:businessName")} *</Label>
              <Input
                id="name"
                data-testid="business-name-input"
                placeholder={t("common:ph_ornProbodyFitnessCenter")}
                value={formData.name}
                onChange={(e) => updateFormData("name", e.target.value)}
                className="h-12 rounded-xl"
              />
            </div>

            <div>
              <Label htmlFor="phone">{t("business:phoneNumber")} *</Label>
              <Input
                id="phone"
                data-testid="business-phone-input"
                placeholder="05XX XXX XX XX"
                value={formData.phone}
                onChange={(e) => updateFormData("phone", e.target.value)}
                className="h-12 rounded-xl"
              />
            </div>

            <div>
              <Label htmlFor="email">{t("business:emailAddress")}</Label>
              <Input
                id="email"
                data-testid="business-email-input"
                type="email"
                placeholder="info@isletme.com"
                value={formData.email}
                onChange={(e) => updateFormData("email", e.target.value)}
                className="h-12 rounded-xl"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Google Maps Location Picker - Tek başına yeterli */}
            <div>
              <Label className="mb-2 block">{t("business:businessLocation")} *</Label>
              <p className="text-sm text-grey mb-3">{t("employees:haritadaIsletmenizinKonumunuArayinVeya")}</p>
              <GoogleMapLocationPicker
                onLocationSelect={(location) => {
                  // Update form with selected location
                  updateFormData("latitude", location.lat);
                  updateFormData("longitude", location.lng);
                  updateFormData("address", location.formatted_address || "");
                  
                  if (location.neighborhood) {
                    updateFormData("neighborhood", location.neighborhood);
                  }
                  if (location.street) {
                    updateFormData("street", location.street);
                  }
                  if (location.building_no) {
                    updateFormData("building_no", location.building_no);
                  }
                  if (location.district) {
                    updateFormData("district", location.district);
                  }
                  if (location.city) {
                    // İstanbul için (Avrupa) veya (Anadolu) kısmını kaldır
                    let cityName = location.city;
                    if (cityName.includes("İstanbul")) {
                      cityName = "İstanbul";
                      updateFormData("city", "istanbul");
                    } else {
                      const matchedCity = PRIORITY_CITIES.find(c => 
                        c.name.toLowerCase().includes(cityName.toLowerCase())
                      );
                      if (matchedCity) {
                        updateFormData("city", matchedCity.code);
                      } else {
                        // Şehir listesinde yoksa direkt kaydet
                        updateFormData("city", cityName.toLowerCase());
                      }
                    }
                  }
                }}
                initialLocation={{ lat: 41.0082, lng: 28.9784 }}
                initialAddress={formData.address}
                height="400px"
              />
            </div>

            {/* Seçilen Konum Bilgisi */}
            {formData.address && (
              <div className="bg-green-50 border border-green-200 p-4 rounded-xl">
                <p className="text-sm font-medium text-green-800 mb-1">{t("common:secilenKonum")}</p>
                <p className="text-sm text-green-700">{formData.address}</p>
                {formData.city && (
                  <p className="text-xs text-green-600 mt-1">
                    {formData.district && `${formData.district}, `}
                    {formData.city.charAt(0).toUpperCase() + formData.city.slice(1)}
                  </p>
                )}
              </div>
            )}

            {/* Manuel Adres Düzenleme (Opsiyonel) */}
            <div className="bg-slate-50 p-4 rounded-xl space-y-4">
              <p className="text-sm font-medium text-slate-700">
                {t("business:manualEdit")}
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="neighborhood" className="text-xs">{t("business:neighborhood")}</Label>
                  <Input
                    id="neighborhood"
                    placeholder={t("common:ph_mahalleAdi")}
                    value={formData.neighborhood}
                    onChange={(e) => updateFormData("neighborhood", e.target.value)}
                    className="h-10 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="street" className="text-xs">{t("business:streetAddress")}</Label>
                  <Input
                    id="street"
                    placeholder={t("common:ph_caddesokakAdi")}
                    value={formData.street}
                    onChange={(e) => updateFormData("street", e.target.value)}
                    className="h-10 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="building_no" className="text-xs">{t("business:buildingNo")}</Label>
                  <Input
                    id="building_no"
                    placeholder="No"
                    value={formData.building_no}
                    onChange={(e) => updateFormData("building_no", e.target.value)}
                    className="h-10 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Branches */}
            <div>
              <Label className="mb-3 block">{t('common:branches')} *</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {branches.map((branch) => (
                  <button
                    key={branch.id}
                    type="button"
                    data-testid={`branch-${branch.id}`}
                    className={`p-3 rounded-xl border-2 text-sm transition-all ${
                      formData.branches.includes(branch.name)
                        ? "border-navy bg-navy/5 text-navy"
                        : "border-grey-200 hover:border-grey-300 text-grey-700"
                    }`}
                    onClick={() => toggleBranch(branch.name)}
                  >
                    {getLocalizedBranch(branch.name)}
                  </button>
                ))}
              </div>
            </div>

            {/* Training Types */}
            <div>
              <Label className="mb-3 block">{t('common:trainingTypes')} *</Label>
              <div className="grid grid-cols-3 gap-3">
                {trainingTypes.map((type) => (
                  <button
                    key={type.id || type.name}
                    type="button"
                    data-testid={`training-${type.id || type.name}`}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      formData.training_types.includes(type.id) || formData.training_types.includes(type.name)
                        ? "border-navy bg-navy/5 text-navy"
                        : "border-grey-200 hover:border-grey-300 text-grey-700"
                    }`}
                    onClick={() => toggleTrainingType(type.name || type.id)}
                  >
                    {getLocalizedTrainingType(type.name || type.label || type.id)}
                  </button>
                ))}
              </div>
            </div>

            {/* Working Hours */}
            <div>
              <Label className="mb-3 block">{t('settings:workingHours')}</Label>
              <div className="space-y-2">
                {Object.entries(workingHours).map(([day, hours]) => {
                  const dayNames = {
                    monday: t("business:monday"),
                    tuesday: t("common:tuesday"),
                    wednesday: t("common:wednesday"),
                    thursday: t("common:thursday"),
                    friday: t("business:friday"),
                    saturday: t("business:saturday"),
                    sunday: t("business:sunday"),
                  };
                  return (
                    <div
                      key={day}
                      className={`flex items-center gap-3 p-3 rounded-xl ${
                        hours.isOpen ? "bg-green-50" : "bg-grey-50"
                      }`}
                    >
                      <Checkbox
                        checked={hours.isOpen}
                        onCheckedChange={(checked) =>
                          updateWorkingHour(day, "isOpen", checked)
                        }
                      />
                      <span className="w-24 font-medium">{dayNames[day]}</span>
                      {hours.isOpen ? (
                        <div className="flex items-center gap-2">
                          <Select
                            value={hours.open}
                            onValueChange={(v) => updateWorkingHour(day, "open", v)}
                          >
                            <SelectTrigger className="w-28 h-9">
                              <SelectValue placeholder={t("business:opening")} />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {Array.from({ length: 48 }, (_, i) => {
                                const hour = Math.floor(i / 2);
                                const minute = (i % 2) * 30;
                                const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                                return (
                                  <SelectItem key={time} value={time}>
                                    {time}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <span>-</span>
                          <Select
                            value={hours.close}
                            onValueChange={(v) => updateWorkingHour(day, "close", v)}
                          >
                            <SelectTrigger className="w-28 h-9">
                              <SelectValue placeholder={t("business:closing")} />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {Array.from({ length: 48 }, (_, i) => {
                                const hour = Math.floor(i / 2);
                                const minute = (i % 2) * 30;
                                const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                                return (
                                  <SelectItem key={time} value={time}>
                                    {time}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <span className="text-grey">{t("business:closed")}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Accepted Cards */}
            <div>
              <Label className="mb-3 block">{t("common:gecerliKartlar")}</Label>
              <div className="flex flex-wrap gap-2">
                {acceptedCards.map((card) => (
                  <button
                    key={card}
                    type="button"
                    className={`px-4 py-2 rounded-full text-sm transition-all ${
                      formData.accepted_cards.includes(card)
                        ? "bg-navy text-white"
                        : "bg-grey-100 text-grey-700 hover:bg-grey-200"
                    }`}
                    onClick={() => toggleCard(card)}
                  >
                    {card}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <Label className="mb-3 block">{t("business:businessLogo")} *</Label>
              <p className="text-sm text-grey mb-4">
                İşletmeniz için bir logo ekleyin.
              </p>

              <div className="flex items-center gap-6">
                {formData.images.length > 0 ? (
                  <div className="relative">
                    <div className="w-32 h-32 rounded-xl overflow-hidden border-2 border-slate-200">
                      <img
                        src={formData.images[0]}
                        alt={t("common:businessLogo")}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-md"
                      onClick={() => removeImage(0)}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="add-image-btn"
                    className="w-32 h-32 rounded-xl border-2 border-dashed border-grey-300 flex flex-col items-center justify-center gap-2 hover:border-navy hover:bg-navy/5 transition-colors"
                    onClick={handleImageUpload}
                  >
                    <Upload className="w-6 h-6 text-grey" />
                    <span className="text-xs text-grey">{t("business:addLogo")}</span>
                  </button>
                )}
                
                <div className="text-sm text-grey">
                  <p>• Önerilen boyut: 200x200 piksel</p>
                  <p>• Kare format tercih edilir</p>
                  <p>• Max 10MB</p>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="bg-grey-50 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-navy mb-4">{t("common:ozet")}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-grey">{t("business:businessNameLabel")}</span>
                  <span className="font-medium">{formData.name || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-grey">{t("business:type")}:</span>
                  <span className="font-medium">
                    {formData.business_type === "corporate" ? t("business:corporate") : t("business:individual")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-grey">{t("business:locationLabel")}:</span>
                  <span className="font-medium">{formData.city || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-grey">{t("business:branchesLabel")}</span>
                  <span className="font-medium">{formData.branches.length} {t("business:count")}</span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={isBranchCreation ? "/dashboard/branches" : "/dashboard"}
            className="inline-flex items-center gap-2 text-grey hover:text-navy mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {isBranchCreation ? t("common:backToBranches") : t("common:backToDashboard")}
          </Link>
          <h1 className="font-heading font-bold text-2xl text-navy">
            {isBranchCreation ? t("common:addNewBranch") : t("common:addNewBusiness")}
          </h1>
          <p className="text-grey mt-1">
            {isBranchCreation 
              ? t("business:addBranchToParent", { name: parentBusiness?.name || '' })
              : t("common:registerYourBusiness")
            }
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS_KEYS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    currentStep >= step.id
                      ? "bg-navy text-white"
                      : "bg-grey-200 text-grey"
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                {index < STEPS_KEYS.length - 1 && (
                  <div
                    className={`hidden md:block w-24 h-1 mx-2 rounded-full transition-colors ${
                      currentStep > step.id ? "bg-navy" : "bg-grey-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="hidden md:flex justify-between mt-2">
            {STEPS_KEYS.map((step) => (
              <span
                key={step.id}
                className={`text-sm ${
                  currentStep >= step.id ? "text-navy font-medium" : "text-grey"
                }`}
              >
                {t(step.titleKey)}
              </span>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading">
              {t(STEPS_KEYS[currentStep - 1]?.titleKey)}
            </CardTitle>
            <CardDescription>
              {t("business:step")} {currentStep} / {STEPS_KEYS.length}
            </CardDescription>
          </CardHeader>
          <CardContent>{renderStepContent()}</CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("business:back")}
          </Button>

          {currentStep < STEPS_KEYS.length ? (
            <Button
              className="bg-navy hover:bg-navy/90 rounded-xl"
              onClick={handleNext}
              data-testid="next-step-btn"
            >
              {t("business:next")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              className="bg-red-500 hover:bg-red-600 rounded-xl"
              onClick={handleSubmit}
              disabled={loading}
              data-testid="submit-business-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {isBranchCreation ? t("common:saveBranch") : t("common:saveBusiness")}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
